import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { TaxComponent, TaxComponentCreate, TaxComponentUpdate } from '@/app/types/taxComponent';

// Zod Schema for Tax Component Form
const formSchema = z.object({
  name: z.string().min(1, 'Component name is required').max(50),
  code: z.string().min(1, 'Component code is required').max(20)
    .refine(s => !/\s/.test(s) && /^[A-Z0-9_]+$/.test(s), 'Code must be uppercase letters, numbers, or underscores, with no spaces'),
  description: z.string().optional().nullable(),
});

type TaxComponentFormValues = z.infer<typeof formSchema>;

interface TaxComponentFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: TaxComponentCreate | TaxComponentUpdate) => void;
  defaultValues?: Partial<TaxComponent>;
  isLoading?: boolean;
  mode: 'create' | 'edit';
}

const TaxComponentForm: React.FC<TaxComponentFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
}) => {
  const form = useForm<TaxComponentFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name || '',
      code: defaultValues?.code || '',
      description: defaultValues?.description || '',
    },
  });

  // Disable code editing in edit mode
  const isCodeDisabled = mode === 'edit';

  const handleSubmit: SubmitHandler<TaxComponentFormValues> = (values) => {
     const dataToSend = mode === 'edit'
      ? { name: values.name, description: values.description } // Only send updatable fields for edit
      : values; // Send all fields for create
    onSubmit(dataToSend);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{mode === 'create' ? 'Add New Tax Component' : 'Edit Tax Component'}</DialogTitle>
          <DialogDescription>
            {mode === 'create' ? 'Define a new tax component (e.g., CGST, CESS).' : 'Update the component details.'}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., Central GST" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="code"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Code*</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="e.g., CGST" disabled={isCodeDisabled} />
                  </FormControl>
                  {isCodeDisabled && (
                    <FormDescription>Code cannot be changed after creation.</FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Enter description"
                      value={field.value || ''}
                      rows={3}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {isLoading
                  ? (mode === 'create' ? 'Creating...' : 'Updating...')
                  : (mode === 'create' ? 'Create Component' : 'Save Changes')
                }
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default TaxComponentForm; 